// Owner: shell-nav. Whether the app has a route at a path (the rail links to `/library` and `/account`
// once their owners ship them, and to the older screens until then — no dead links in between).
import { useRouter } from '@tanstack/react-router';
import { useMemo } from 'react';

/** What `routeExists` reads of the router: every route by its id, with its full path. */
type RouterLike = { routesById: object };

const fullPathOf = (route: unknown): string | null =>
  typeof route === 'object' && route !== null && 'fullPath' in route && typeof route.fullPath === 'string'
    ? route.fullPath
    : null;

export function routeExists(router: RouterLike, path: string): boolean {
  const want = path.replace(/\/+$/, '');
  return Object.values(router.routesById).some((r) => fullPathOf(r)?.replace(/\/+$/, '') === want);
}

/** `hasRoute(path)` for the current router. */
export function useHasRoute(): (path: string) => boolean {
  const router = useRouter();
  return useMemo(() => {
    const cache = new Map<string, boolean>();
    return (path: string) => {
      let v = cache.get(path);
      if (v === undefined) cache.set(path, (v = routeExists(router, path)));
      return v;
    };
  }, [router]);
}
